import { Box, Typography } from '@mui/material';
import { LoadingScreen } from '@truvhq/ui';

import Logo from 'components/Logo';
import { TenantCompanies } from 'features/tenancy/ui/TenantCompanies';
import { useGetCompany, useGetTenantCompanies } from 'hooks/company';
import { useGetWidgetSettings } from 'hooks/customization';
import { useAuth } from 'shared/lib/auth/useAuth';
import { useTenancyContext } from 'shared/lib/tenancy/useTenancyContext';
import { AuthLayoutTemplate } from 'shared/ui/AuthLayoutTemplate';

export type UnapprovedCompanyMemberProps = {
  declined: boolean;
};

export const UnapprovedCompanyMember = ({ declined }: UnapprovedCompanyMemberProps) => {
  const maskEmailAddress = (email: string): string => {
    const [localPart, domain] = email.split('@');
    
    if (!domain) {
      return email;
    }
    
    if (localPart.length <= 2) {
      const first = localPart.charAt(0);
      
      return `${first}**@${domain}`;
    }
    
    const start = localPart.slice(0, 2);
    const end = localPart.slice(-1);
    
    return `${start}**${end}@${domain}`;
  };

  const { user } = useAuth();
  const { data: settings, isPending: areSettingsLoading } = useGetWidgetSettings();
  const { data: company, isFetching: isCompanyFetching } = useGetCompany();
  const { data: tenantCompanies } = useGetTenantCompanies();
  const { setTenantCompanyId } = useTenancyContext();

  const hasTenantCompanies = tenantCompanies && tenantCompanies.length >= 1;
  const currentTenantCompanyId = user?.company?.id;
  const currentTenantCompany = tenantCompanies?.find((c) => c.id === currentTenantCompanyId);
  const maskedOwnerEmail = currentTenantCompany?.owner_email
    ? maskEmailAddress(currentTenantCompany.owner_email)
    : null;

  if (areSettingsLoading || isCompanyFetching) {
    return <LoadingScreen />;
  }

  const companyName = user?.company?.name || company?.name;

  return (
    <AuthLayoutTemplate showLogo={!settings?.logo} pageName="UnapprovedCompanyMember">
      {settings?.logo && (
        <Box className="mb-6 text-center">
          <Logo className="object-contain" size="large" src={settings.logo} />
        </Box>
      )}

      <div className="flex flex-col gap-6 ">
        <Typography variant="h1">{declined ? 'Your account has been declined' : 'Account pending approval'}</Typography>
        <Typography variant="body1">
          {declined ? (
            `Your account access to ${companyName} has been declined. If you have questions, reach out to an Owner or Administrator in ${companyName}.`
          ) : (
            <>
              {`Your request has been sent to your company’s Truv Dashboard Owner`}
              {maskedOwnerEmail && (
                <> ({<strong>{maskedOwnerEmail}</strong>})</>
              )}
              {`. Reach out to them or a Dashboard Admin to approve your account. Once approved, you’ll receive an email confirmation and be able to log in.`}
            </>
          )}
        </Typography>

        {hasTenantCompanies && (
          <Box className="flex flex-col gap-2 pt-4">
            <Typography variant="body1">You can switch to your other companies:</Typography>
            <TenantCompanies
              tenantCompanies={tenantCompanies}
              onSelectCompany={setTenantCompanyId}
              variant="light"
              placeholder="Select company"
              popoverDirection="bottom"
              autoSelectFirst={false}
            />
          </Box>
        )}
        <Typography className="text-grey60" variant="body2">
          Need help? Reach out to{' '}
          <a className="text-primary-main" href="mailto:support@truv.com">
            support@truv.com
          </a>
          .
        </Typography>
      </div>
    </AuthLayoutTemplate>
  );
};
